import api from 'api';
import axios from 'axios';
import { MainSection, MainSectionContainer } from 'components/general-styles';
import ProfileTabsBox from 'components/main/profile/ProfileTabsBox/ProfileTabsBox';
import EventNotes from 'components/main/profile/event-notes/EventNotes';
import StickyNotes from 'components/main/profile/sticky-notes/StickyNotes';
import SummaryBox from 'components/main/profile/summary-box/SummaryBox';
import Toast from 'components/toast';
import { SEND_GRID_MISSED_APPOINTMENTS_PARAM } from 'globalConstants';
import ClinicsServiceClient from 'node-api/clinics/clinics.client';
import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { MixpanelWrapperInstance } from 'services/mixpanel/mixpanel-wrapper';
import { ClickElementTypes } from 'services/mixpanel/mixpanel.types';
import {
  RpmEventTrackingService,
  rpmEventTrackingServiceInstance,
} from 'services/rpm/rpm-track-event-service';
import { PatientContext } from 'state/contexts/patient';
import { ProviderContext } from 'state/contexts/provider';
import { UserContext } from 'state/contexts/user';
import { ActionTypes as PatientActionTypes } from 'state/reducers/patient';
import { ActionTypes as ProviderActionTypes } from 'state/reducers/provider';
import {
  debug,
  getCurrentProviderState,
  isCurrentProviderHidden,
  isFromSendGridMissedAppointments,
} from 'utils/helpers';
import { DEFAULT_TIMEZONE } from 'utils/timezones';
import Chat from '../profile/chat/Chat';
import { getUserInfo } from '../rpm-event/util';

type Props = RouteComponentProps<{ id: string }>;

const PatientPage: React.FC<Props> = ({ match, location }) => {
  const { providerDispatch } = useContext(ProviderContext);
  const { patientState, patientDispatch } = useContext(PatientContext);
  const patient = patientState.current;
  const { userState } = useContext(UserContext);

  const [loaded, setLoaded] = useState<boolean>(false);

  const [user] = useState(getUserInfo(userState));
  const [fromSendGrid] = useState(isFromSendGridMissedAppointments(location.search));

  useEffect(() => {
    const source = axios.CancelToken.source();
    const source2 = axios.CancelToken.source();
    const { id: uuid } = match.params;

    const currentRoleFound = userState.original_user!.available_logins.find((login) => {
      return login.uuid === userState.uuid;
    });

    const roleName = currentRoleFound
      ? `${currentRoleFound.first_name} ${currentRoleFound.last_name}`
      : userState.clinical_role?.name;

    RpmEventTrackingService.setDefaultValues({
      providerUuid: userState.original_user!.uuid, // always sending the provider uuid
      role: roleName ?? 'unknown',
    });

    const getPatient = async () => {
      let patientData = null;
      try {
        const { data } = await api().get(`/patients/${uuid}?auth_token=AUTH_TOKEN`, {
          cancelToken: source.token,
          params: {
            provider_uuid: userState.uuid,
          },
        });
        patientData = data.patient;
      } catch (err) {
        if (!axios.isCancel(err)) {
          debug(`${err}`);
        }
      }

      if (patientData) {
        const clinicsServiceClient = new ClinicsServiceClient();
        try {
          const clinic = await clinicsServiceClient.getClinic(patientData.clinic_id);
          if (!clinic.timezone) {
            throw new Error(`Clinic ID=${clinic.id} does not have a timezone`);
          }

          patientData.timezone = clinic.timezone;
        } catch (err) {
          patientData.timezone = DEFAULT_TIMEZONE;
          debug(`${err}`);
        }

        patientDispatch({
          type: PatientActionTypes.SET,
          payload: {
            patients: [patientData],
            reviewedPatients: [],
          },
        });

        setLoaded(true);
      }
    };

    if (uuid) {
      getPatient();
      rpmEventTrackingServiceInstance.sendVisitProfileEvent(uuid);
    }

    return () => {
      patientDispatch({ type: PatientActionTypes.CLEAR, payload: undefined });
      source.cancel();
      source2.cancel();
    };
  }, [match]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    const getProviders = async () => {
      const selectedPatient = patientState.current;
      if (!selectedPatient) {
        return;
      }

      try {
        const { data } = await api().get(`users/${selectedPatient.id}/providers/AUTH_TOKEN`, {
          cancelToken: source.token,
        });

        if (isCurrentProviderHidden(getCurrentProviderState(userState, data))) {
          Toast.show('error', `You are not allowed to view this patient profile!`);
          return;
        }

        providerDispatch({
          type: ProviderActionTypes.SET,
          payload: {
            providers: data,
          },
        });
      } catch (err) {
        if (!axios.isCancel(err)) {
          debug(`${err}`);
        }
      }
    };

    if (loaded) {
      getProviders();
    }

    return () => {
      source.cancel();
    };
  }, [patientState, loaded]);

  useEffect(() => {
    if (fromSendGrid && !window.history.state?.[SEND_GRID_MISSED_APPOINTMENTS_PARAM.name]) {
      debug('>>> Track click on member link at Sendgrid into Mixpanel');
      window.history.replaceState(
        { ...(window.history.state || {}), [SEND_GRID_MISSED_APPOINTMENTS_PARAM.name]: true },
        ''
      );
      MixpanelWrapperInstance.trackClick({
        name: 'Member link on the missed appointments template at SendGrid',
        type: ClickElementTypes.LINK,
        source: 'SendGrid template',
      });
    }
  }, [fromSendGrid]);

  return (
    <MainSectionContainer>
      {patient && loaded && (
        <>
          <MainSection>
            <StickyNotes key={`sticky-${patient.id}`} />
            <EventNotes key={`event-${patient.id}`} />
            <SummaryBox key={`summary-${patient.id}`} />
            <ProfileTabsBox user={user} />
          </MainSection>

          <Chat key={`chat-${patient.id}`} />
        </>
      )}
    </MainSectionContainer>
  );
};

export default PatientPage;
